import { useState, useRef, useEffect } from "react";
import { Bot, X, Send } from "lucide-react";
import { api, API } from "@/lib/api";

function getSessionId() {
    let id = localStorage.getItem("peppa_chat_session");
    if (!id) {
        id = "chat_" + Math.random().toString(36).slice(2, 12);
        localStorage.setItem("peppa_chat_session", id);
    }
    return id;
}

export default function ChatWidget() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: "assistant", text: "Hi, I'm Peppa! Ask me about our detergents, ICT services, or software development." },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const bodyRef = useRef(null);
    const sessionId = getSessionId();

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, [messages, open]);

    async function send(e) {
        e?.preventDefault();
        const text = input.trim();
        if (!text || loading) return;
        setMessages((m) => [...m, { role: "user", text }]);
        setInput("");
        setLoading(true);
        try {
            const { data } = await api.post("/chat/simple", { session_id: sessionId, text });
            setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
        } catch (err) {
            setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn't reach the server. Please try again or call +256 700 000 000." }]);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    className="fixed right-5 bottom-5 z-40 w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-2xl hover:scale-110 hover:bg-primary-hover transition-all"
                    data-testid="chat-open-btn"
                    aria-label="Open AI chat"
                >
                    <Bot className="w-6 h-6" />
                </button>
            )}
            {open && (
                <div
                    className="fixed right-5 bottom-5 z-40 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-2rem)] bg-white border border-border shadow-2xl flex flex-col"
                    data-testid="chat-widget"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-ink text-white">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                                <Bot className="w-4 h-4" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold">Peppa AI Support</div>
                                <div className="text-[10px] text-white/60 uppercase tracking-wider">Online · Claude Sonnet 4.5</div>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white" data-testid="chat-close-btn">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-surface">
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap ${
                                    m.role === "user"
                                        ? "ml-auto bg-primary text-white"
                                        : "bg-white border border-border text-ink"
                                }`}
                                data-testid={`chat-msg-${m.role}`}
                            >
                                {m.text}
                            </div>
                        ))}
                        {loading && (
                            <div className="bg-white border border-border px-3 py-2 text-sm text-muted-foreground inline-block">
                                <span className="inline-block w-1.5 h-1.5 bg-muted-foreground rounded-full animate-pulse mr-1"></span>
                                <span className="inline-block w-1.5 h-1.5 bg-muted-foreground rounded-full animate-pulse mr-1" style={{ animationDelay: "0.2s" }}></span>
                                <span className="inline-block w-1.5 h-1.5 bg-muted-foreground rounded-full animate-pulse" style={{ animationDelay: "0.4s" }}></span>
                            </div>
                        )}
                    </div>
                    <form onSubmit={send} className="border-t border-border p-3 flex gap-2 bg-white">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Type your question…"
                            className="flex-1 border border-border px-3 py-2 text-sm focus:outline-none focus:border-primary"
                            data-testid="chat-input"
                            disabled={loading}
                        />
                        <button
                            type="submit"
                            disabled={loading || !input.trim()}
                            className="bg-primary text-white px-3 hover:bg-primary-hover disabled:opacity-50 transition-colors"
                            data-testid="chat-send-btn"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
